/**
 * SiteVisitDetailPage — R2 redirect shim.
 *
 * Looks up the visit's project_id and redirects to the inline section on the
 * project detail page, which has the full R2/R4 affordances. The stale
 * standalone UI is not maintained; a future spec will redesign it.
 */
import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Loader2 } from 'lucide-react';
import { useSiteVisit } from '@/hooks/useSiteVisits';

export function SiteVisitDetailPage() {
  const { visitId } = useParams<{ visitId: string }>();
  const navigate = useNavigate();

  const { data, isLoading, isError, error, refetch } = useSiteVisit(visitId ?? null);

  useEffect(() => {
    if (!data) return;
    const projectId = data.visit?.project_id;
    if (!projectId) return;
    navigate(`/projects/${projectId}`, {
      replace: true,
      state: { expandVisit: visitId },
    });
  }, [data, visitId, navigate]);

  if (isLoading) {
    return (
      <DashboardLayout title="Site Visit">
        <div className="py-12 flex items-center justify-center text-sm text-gray-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Loading…
        </div>
      </DashboardLayout>
    );
  }

  if (isError) {
    const status = (error as any)?.status;
    const is5xx = !status || status >= 500;
    return (
      <DashboardLayout title="Site Visit">
        <div className="py-12 text-center text-sm text-gray-500">
          {is5xx ? (
            <>
              <p className="text-gray-700 font-medium">Service temporarily unavailable</p>
              <p className="mt-1 text-gray-400">The server encountered an error. Please try again.</p>
              <button
                onClick={() => refetch()}
                className="mt-3 rounded-md bg-purple-600 px-4 py-2 text-sm text-white hover:bg-purple-700"
              >
                Retry
              </button>
            </>
          ) : (
            <>
              <p>Visit not found</p>
              <button onClick={() => navigate(-1)} className="mt-3 text-blue-600 text-sm hover:underline">
                Go back
              </button>
            </>
          )}
        </div>
      </DashboardLayout>
    );
  }

  if (!isLoading && !data) {
    return (
      <DashboardLayout title="Site Visit">
        <div className="py-12 text-center text-sm text-gray-500">
          <p>Visit not found</p>
          <button onClick={() => navigate(-1)} className="mt-3 text-blue-600 text-sm hover:underline">
            Go back
          </button>
        </div>
      </DashboardLayout>
    );
  }

  // Render nothing while the redirect fires
  return null;
}

export default SiteVisitDetailPage;
